/**
 * Spintax v2 Configuration
 * Central configuration for all Spintax behavior
 * 
 * This is the single source of truth for all Spintax v2 settings.
 * Changes here automatically apply to both frontend and backend.
 * 
 * @module spintax-v2/config
 * @version 2.0.0
 */

const SPINTAX_CONFIG = {
  // ============================================
  // FEATURE FLAGS
  // ============================================
  
  /**
   * Enable Spintax processing for email subjects
   * @type {boolean}
   */
  SUBJECT_SPINNING_ENABLED: false,
  
  /**
   * Enable Spintax processing for email bodies
   * @type {boolean}
   */
  BODY_SPINNING_ENABLED: true,
  
  // ============================================
  // GENERATION SETTINGS
  // ============================================
  
  /**
   * Default number of variations to generate
   * @type {number}
   */
  DEFAULT_VARIATION_COUNT: 5,
  
  /**
   * Maximum number of variations allowed
   * @type {number}
   */
  MAX_VARIATION_COUNT: 10,
  
  /**
   * Minimum number of variations allowed
   * @type {number}
   */
  MIN_VARIATION_COUNT: 3,
  
  // ============================================
  // VALIDATION SETTINGS
  // ============================================
  
  /**
   * Maximum attempts to generate unique variations
   * @type {number}
   */
  MAX_ATTEMPTS: 20,
  
  /**
   * Minimum uniqueness threshold (0.0 - 1.0)
   * e.g., 0.8 means at least 80% of variations must be unique
   * @type {number}
   */
  MIN_UNIQUENESS_THRESHOLD: 0.8,
  
  /**
   * Warn if uniqueness ratio falls below this threshold
   * @type {number}
   */
  UNIQUENESS_WARNING_THRESHOLD: 0.9,
  
  // ============================================
  // UI SETTINGS
  // ============================================
  
  /**
   * Show raw Spintax syntax (e.g., {book|schedule}) in preview modal
   * @type {boolean}
   */
  SHOW_RAW_SPINTAX_IN_PREVIEW: false,
  
  /**
   * Allow users to manually edit Spintax syntax
   * @type {boolean}
   */
  ALLOW_MANUAL_SPINTAX_EDIT: false,
  
  /**
   * Show metadata in preview modal (generation stats, uniqueness, etc.)
   * @type {boolean}
   */
  SHOW_METADATA_IN_PREVIEW: true,
  
  // ============================================
  // PROCESSING SETTINGS
  // ============================================
  
  /**
   * Preserve formatting (line breaks, spacing) during processing
   * @type {boolean}
   */
  PRESERVE_FORMATTING: true,
  
  /**
   * Match words case-sensitively
   * @type {boolean}
   */
  CASE_SENSITIVE: false,
  
  /**
   * Trim whitespace from individual options within {|}
   * @type {boolean}
   */
  TRIM_OPTIONS: true,
  
  // ============================================
  // STORAGE SETTINGS
  // ============================================
  
  /**
   * Store raw Spintax format in database (in addition to resolved variations)
   * @type {boolean}
   */
  STORE_RAW_SPINTAX: false,
  
  /**
   * Store metadata with campaign
   * @type {boolean}
   */
  STORE_METADATA: true,
  
  // ============================================
  // LOGGING & DEBUGGING
  // ============================================
  
  /**
   * Enable debug mode (verbose logging)
   * Automatically enabled in development environment
   * @type {boolean}
   */
  DEBUG_MODE: typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development',
  
  /**
   * Log prefix for all Spintax v2 messages
   * @type {string}
   */
  LOG_PREFIX: '🌀 [Spintax v2]',
  
  /**
   * Enable performance tracking
   * @type {boolean}
   */
  TRACK_PERFORMANCE: true,
  
  // ============================================
  // VERSION INFO
  // ============================================
  
  /**
   * Spintax v2 version
   * @type {string}
   */
  VERSION: '2.0.0',
  
  /**
   * Build timestamp
   * @type {string}
   */
  BUILD_DATE: new Date().toISOString()
};

// Export for both CommonJS (Node.js) and ES6 (React)
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { SPINTAX_CONFIG };
}

if (typeof exports !== 'undefined') {
  exports.SPINTAX_CONFIG = SPINTAX_CONFIG;
}

// ES6 export (will be ignored in Node.js)
if (typeof window !== 'undefined') {
  // Browser environment - make available globally if needed
  window.SPINTAX_V2_CONFIG = SPINTAX_CONFIG;
}

// Default export for ES6
export { SPINTAX_CONFIG };
export default SPINTAX_CONFIG;

